import { Button, Form, FormRule, Input, Spin } from "antd";
import { message } from "utils/ciq-message";
import { useContext, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { useMutation } from "@apollo/client";
import { MUTATION_PROJECT_DEFAULT_CONFIG } from "services/graphQL/mutations";
import { ProjectContext } from "context/ProjectProvider";
import { FeatureTypes } from "entity-app/constants";

const renameFieldsInfo = (page: string) => (
  <div className="flex items-center w-full info-label pl-[25px]">
    <div className="flex text-justify">
      Rename the column headers of the {page} log
    </div>
  </div>
);

type TRenameData = {
  page: string;
  headerTitleMap: any;
};

const renameEntityData = {
  [FeatureTypes.SUBMITTALS]: {
    page: "Submittal",
    headerTitleMap: {
      submittal_id: "ID",
      spec_no: "Spec Section",
      submittal_number: "Submittal Number",
      title: "Submittal Title",
      description: "Description",
      revision: "Revision",
      type: "Type",
      workflow_status: "Status",
      state: "State",
      assignee: "Ball In Court",
      responsible_contractor: "Responsible Contractor",
      submitter: "Submitter",
      gc_reviewer: "GC Reviewer",
      design_reviewer: "Design Reviewer",
      assigned_to_subcontractor: "Assigned To Subcontractor (Actual)",
      submitted_by_subcontractor: "Submitted By Subcontractor (Actual)",
      submitted_for_design_review: "Submitted For Design Review (Actual)",
      design_review_completed: "Design Review Completed (Actual)",
      submittal_distributed: "Submittal Distributed (Actual)",
      linked_activities: "Linked Activities",
      linked_materials: "Linked Materials",
      next_planned_deadline: "Next Deadline",
      next_action: "Next Action",
      due_date: "Due Date",
      risk_level: "Risk",
      created_at: "Created At",
      priority: "Priority",
      watchers: "Watchers",
      float: "Submittal Float",
      manual_entry_date: "Deadline Date",
      final_deadline_date: "Deadline",
      projected_last_milestone_date: "Projected Completion Date",
      planned_next_milestone_date: "Planned Date Of Next Milestone",
      projected_next_milestone_date: "Projected Date Of Next Milestone",
      not_found_in_source: "Missing In Sync",
      review_response: "Review response",
      final_response: "Final response",
      procurement_ready: "Procurement Readiness",
      last_manual_updated_at: "Last Updated by User",
      last_system_updated_at: "Last Updated by System"
    }
  },
  [FeatureTypes.MATERIALS]: {
    page: "Material",
    headerTitleMap: {
      material_id: "ID",
      name: "Material Title",
      description: "Material Description",
      status: "Status",
      next_planned_deadline: "Next Deadline",
      due_date: "Due Date",
      risk_level: "Risk",
      float: "Material Float",
      spec_section_no: "Spec Section",
      tag_name: "Material Tag",
      date_block_template_name: "Material Template",
      quantity: "Quantity",
      quantity_unit_id: "Quantity Unit",
      trade_partner: "Responsible Contractor",
      assignee: "Assignee",
      gc_representative: "GC Representative",
      manufacturer: "Manufacturer",
      linked_activities: "Linked Activities",
      linked_submittals: "Linked Submittals",
      material_size: "Material Size",
      size_unit_id: "Material Size Unit",
      part_number: "Part Number",
      serial_number: "Serial Number",
      next_action: "Next Action",
      warranty: "Warranty (In Years)",
      created_at: "Created At",
      wf_override_reason: "Released Early",
      shipping_location: "Shipping Location",
      locations: "Location",
      purchase_order: "Purchase Order",
      manual_entry_date: "Deadline Date",
      final_deadline_date: "Deadline",
      projected_last_milestone_date: "Projected Completion Date",
      lead_time: "Total Lead Time",
      planned_next_milestone_date: "Planned Date Of Next Milestone",
      projected_next_milestone_date: "Projected Date Of Next Milestone",
      linked_materials: "Linked Materials",
      source: "Source",
      division: "Division",
      ready_for_release: "Ready for Release",
      last_manual_updated_at: "Last Updated by User",
      last_system_updated_at: "Last Updated by System"
    }
  },
  [FeatureTypes.BID_PACKAGE]: {
    page: "Bid Package",
    headerTitleMap: {
      id: "ID",
      number: "Number",
      title: "Title",
      wave: "Wave",
      workflow_status: "Status",
      next_deadline: "Next Deadline",
      due_date: "Due Date",
      risk_level: "Risk",
      float: "Bid Package Float",
      workflow_template_id: "Bid Package Template",
      no_of_bidders: "No of Bidders",
      bidding_subcontractors: "Bidding Subcontractors",
      preconstruction_lead: "Preconstruction Lead",
      operations_lead: "Operations Lead",
      estimated_value: "Estimated Value",
      awarded_value: "Awarded Value",
      awarded_subcontractor_name: "Awarded Subcontractor Name",
      ready_for_release: "Early Bid Package",
      manual_entry_date: "Deadline Date",
      onsite_estimate_date: "Onsite Estimate",
      final_deadline_date: "Deadline",
      projected_last_milestone_date: "Projected Completion Date",
      planned_next_milestone_date: "Planned Date Of Next Milestone",
      projected_next_milestone_date: "Projected Date Of Next Milestone",
      last_manual_updated_at: "Last Updated by User",
      last_system_updated_at: "Last Updated by System"
    }
  },
  [FeatureTypes.DESIGN_PACKAGE]: {
    page: "Design Package",
    headerTitleMap: {
      id: "ID",
      number: "Number",
      title: "Title",
      description: "Description",
      responsible_designer_id: "Responsible Design Firm",
      design_firm_id: "Responsible Designer",
      workflow_status: "Status",
      next_deadline: "Next Deadline",
      due_date: "Due Date",
      risk: "Risk",
      float: "Float",
      workflow_template_id: "Workflow Template ID",
      milestones: "Milestones",
      gc_representative_id: "GC Representative",
      manual_entry_date: "Deadline Date",
      final_deadline_date: "Deadline",
      projected_last_milestone_date: "Projected Completion Date",
      planned_next_milestone_date: "Planned Date Of Next Milestone",
      projected_next_milestone_date: "Projected Date Of Next Milestone",
      last_manual_updated_at: "Last Updated by User",
      last_system_updated_at: "Last Updated by System"
    }
  },
  [FeatureTypes.DESIGN_SCOPE]: {
    page: "Design Scope",
    headerTitleMap: {
      sequence_id: "ID",
      title: "Title",
      description: "Description",
      uniformat: "UniFormat",
      masterformat: "MasterFormat",
      classification_code: "Classification Code",
      scope_item_count: "No. of Scope Items",
      linked_design_package: "Linked Design Package",
      design_package: "Design Package",
      milestone: "Milestone",
      lod: "LOD",
      bid_package: "Bid Package"
    }
  }
} as { [key in FeatureTypes]: TRenameData };

function FeatureRenamedFieldSettings({
  featureId,
  columnHeader,
  disabled,
  hiddenFieldKeys = []
}: {
  featureId: FeatureTypes;
  columnHeader: any;
  disabled: boolean;
  hiddenFieldKeys?: string[];
}) {
  const { gqlClientForProject } = useContext(ProjectContext);

  const [form] = Form.useForm();
  const [isFieldsEditable, setFieldsEditable] = useState<boolean>(false);
  const [submitInProgress, setSubmitInProgress] = useState<boolean>(false);
  const [showWarningMsg, setShowWarningMsg] = useState(false);

  const renameData = useMemo(() => {
    return renameEntityData[featureId];
  }, [featureId]);

  const getRenameFieldRules = (currentIndex: number): FormRule[] => [
    {
      required: true,
      message: "Please enter field name",
      validator: async (_, value) => {
        if (value?.trim() === "") {
          setShowWarningMsg(true);
          return Promise.reject(new Error("Please enter field name"));
        }

        setShowWarningMsg(false);
        return Promise.resolve();
      }
    },
    {
      message: "Duplicate field names are not allowed",
      validator: async (_, value) => {
        const trimmed = value?.trim();
        if (!trimmed) return Promise.resolve();

        const fieldMapping = form.getFieldValue("field_mapping") ?? [];
        const captions = (fieldMapping as { caption_en?: string }[]).map(
          (item, idx) =>
            idx === currentIndex ? trimmed : item?.caption_en?.trim()
        );
        const filledCaptions = captions.filter(Boolean);

        const duplicateCount = filledCaptions.filter(
          (caption) => caption?.toLowerCase() === trimmed.toLowerCase()
        ).length;

        if (duplicateCount > 1) {
          setShowWarningMsg(true);
          return Promise.reject(
            new Error("Duplicate field names are not allowed")
          );
        }

        setShowWarningMsg(false);
        return Promise.resolve();
      }
    }
  ];

  const [updateProjectConfigMutation] = useMutation<any>(
    MUTATION_PROJECT_DEFAULT_CONFIG,
    {
      client: gqlClientForProject
    }
  );

  const cancelAction = () => {
    form.resetFields();
    setFieldsEditable(false);
    setSubmitInProgress(false);
  };

  /** Merge sparse Form values (only caption_en is registered) back into full field_mapping. */
  const mergeFieldMappingUpdates = (
    original: Array<Record<string, unknown>>,
    updates: Array<{ caption_en?: string } | undefined | null>
  ) => {
    return original.map((col, index) => {
      const update = updates?.[index];
      return {
        ...col,
        ...(update?.caption_en === undefined
          ? {}
          : { caption_en: update.caption_en })
      };
    });
  };

  const updateRenameFields = (data: any) => {
    // Ant Design Form.List only submits registered Form.Item paths (caption_en).
    // Merge back onto the loaded mapping so field_name and other keys are kept.
    const field_mapping = (columnHeader?.field_mapping ?? []).map(
      (original: Record<string, unknown>, index: number) => ({
        ...original,
        caption_en:
          data?.field_mapping?.[index]?.caption_en ?? original.caption_en
      })
    );

    const variables = {
      feature_id: featureId,
      field_mapping,
      date_block_action: null
    };

    const updateResponse = updateProjectConfigMutation({
      variables
    });

    return updateResponse;
  };

  const onFinishForm = async (data: any) => {
    setSubmitInProgress(true);

    const mergedFieldMapping = mergeFieldMappingUpdates(
      columnHeader?.field_mapping ?? [],
      data?.field_mapping ?? []
    );

    const updateResponse = await updateRenameFields({
      ...data,
      field_mapping: mergedFieldMapping
    });

    if (updateResponse.errors) {
      message.error(updateResponse.errors[0]?.message);
    } else {
      message.success("Default Settings are updated successfully.");
    }
    setFieldsEditable(false);
    setSubmitInProgress(false);
  };

  useEffect(() => {
    form.setFieldsValue(columnHeader);
  }, [columnHeader, form]);

  return (
    <Form
      form={form}
      initialValues={columnHeader}
      onFinish={onFinishForm}
      className="flex flex-col"
      disabled={disabled}
      validateTrigger={["onSubmit", "onChange"]}
      data-testid="rename-fields-settings-form"
    >
      <div className="flex-col pt-[30px] pl-[50px] pr-[50px]">
        <div className="flex w-full items-center justify-between h-[40px] py-[6px] px-6 pr-[95px]">
          <div className="text-ciq-gray-950 font-bold text-xl">
            Rename Fields
          </div>

          {isFieldsEditable ? (
            <div className="flex space-x-4">
              <Button
                className="w-24"
                type="primary"
                htmlType="submit"
                loading={submitInProgress}
                disabled={submitInProgress || showWarningMsg}
                data-testid="rename-fields-settings-button-save"
              >
                Save
              </Button>
              <Button
                className="w-24"
                disabled={submitInProgress || showWarningMsg}
                onClick={cancelAction}
                data-testid="index-button-cancel"
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              type="primary"
              className="w-24"
              disabled={disabled}
              data-testid="index-button-edit"
              onClick={() => setFieldsEditable(true)}
            >
              Edit
            </Button>
          )}
        </div>
        {renameFieldsInfo(renameData.page)}

        <div className="flex justify-start w-full mt-10 overflow-y overflow-x-hidden max-h-[calc(100vh-300px)] pl-[30px]">
          {columnHeader?.field_mapping ? (
            <div className="grid grid-cols-2 py-2 gap-x-10 w-[95%]">
              <Form.List name="field_mapping">
                {(fields) => (
                  <>
                    {fields.map((field, index) => {
                      const titleKey: string =
                        columnHeader?.field_mapping[index]?.field_name;

                      // CIQ-2391 Remove fields which we do not allow renaming of from the settings page
                      const shouldHideField = (hiddenFieldKeys ?? []).includes(
                        titleKey
                      );
                      const fieldWrapperClasses = classNames("pb-[20px]", {
                        hidden:
                          !columnHeader?.field_mapping[index]?.allow_rename ||
                          !renameData.headerTitleMap[titleKey] ||
                          shouldHideField
                      });

                      return (
                        <div
                          className={fieldWrapperClasses}
                          key={`rename-${titleKey}`}
                        >
                          <div className="col-span-1 titles mr-1 ">
                            {renameData.headerTitleMap[titleKey] || ""}
                          </div>
                          <Form.Item
                            {...field}
                            className="col-span-1"
                            key="caption_en"
                            name={[field.name, "caption_en"]}
                            rules={getRenameFieldRules(index)}
                            data-testid={`rename-fields-settings-form-item${titleKey}`}
                          >
                            <Input
                              type="text"
                              disabled={!isFieldsEditable}
                              data-testid={`rename-fields-settings-input-item${titleKey}`}
                            />
                          </Form.Item>
                        </div>
                      );
                    })}
                  </>
                )}
              </Form.List>
            </div>
          ) : (
            <Spin />
          )}
        </div>
      </div>
    </Form>
  );
}

export default FeatureRenamedFieldSettings;
